// Define document categories
export const DOCUMENT_CATEGORIES = {
    BYLAWS: {
        id: "BYLAWS",
        name: "Bylaws",
        description: "The legally binding rules that govern how the strata operates, including restrictions on rentals, pets, noise, smoking, and property use. These documents define the rights and responsibilities of owners and residents.",
    },
    POLICIES: {
        id: "POLICIES",
        name: "Policies and Rules",
        description: "Additional strata policies that supplement bylaws, covering topics like short-term rentals, guest parking rules, move-in/move-out procedures, and strata fee payment policies.",
    },
    STRATA_PLAN: {
        id: "STRATA_PLAN",
        name: "Strata Plan",
        description: "The official, registered document that outlines the layout of the strata property, including unit boundaries, common areas, easements, and shared facilities. Essential for understanding property ownership and legal designations.",
    },
    ALTERATIONS: {
        id: "ALTERATIONS",
        name: "Alterations",
        description: "Documentation of approved and denied renovation requests, detailing modifications to units or common property. Includes conditions for alterations, council approvals, and associated fees or obligations.",
    },
    FINANCIAL_STATEMENTS: {
        id: "FINANCIAL_STATEMENTS",
        name: "Financial Statements",
        description: "Monthly and annual reports detailing the strata's income, expenses, cash reserves, and financial health. These statements help owners assess how well the strata is managing its funds.",
    },
    BUDGETS: {
        id: "BUDGETS",
        name: "Budgets",
        description: "Forecasted financial plans outlining expected income and expenditures for the strata, including operational costs, maintenance expenses, and planned improvements.",
    },
    STRATA_FEES: {
        id: "STRATA_FEES",
        name: "Strata Fees",
        description: "Information on regular monthly or annual fees paid by owners to cover building maintenance, insurance, and operational costs. Includes payment schedules and details on any outstanding fees.",
    },
    SPECIAL_LEVIES: {
        id: "SPECIAL_LEVIES",
        name: "Special Levies",
        description: "Notices and records of one-time or ongoing additional charges imposed on owners to fund major projects like roofing, plumbing upgrades, or structural repairs. Includes levy breakdowns, payment schedules, and voting results.",
    },
    TITLE_DOCUMENTS: {
        id: "TITLE_DOCUMENTS",
        name: "Title Documents",
        description: "Official ownership records, including individual unit titles, common property designations, and registered easements. These documents are essential for verifying legal ownership and property rights.",
    },
    FORM_B_INFORMATION_CERTIFICATE: {
        id: "FORM_B_INFORMATION_CERTIFICATE",
        name: "Form B Information Certificate",
        description: `Form B Information Certificate is a standardized document issued by a strata corporation in British Columbia. It certifies key information about a specific strata lot, including the owner’s name, current strata fees, and any amounts owing. It also confirms whether the strata corporation is involved in legal proceedings or has approved special levies. The form must be signed by a strata council member or authorized agent and is often used in real estate transactions. A Form B information certificate documents contains only the Form B, not other docuemnts like insurancy summaries or finacial information.`,
    },
    FORM_Y_NOTICE_OF_DIFFERENT_BYLAWS: {
        id: "FORM_Y_NOTICE_OF_DIFFERENT_BYLAWS",
        name: "Form Y Notice of Different Bylaws",
        description: `Form Y Notice of Different Bylaws is a standardized document filed by the owner developer when creating a new strata corporation in British Columbia. It registers bylaws that differ from the Standard Bylaws set out in the Strata Property Act. These customized bylaws become legally binding for the strata corporation once registered with the Land Title Office. A Form Y document contains only the Form Y itself, outlining the different or additional bylaws, and does not include other documents like the strata plan or financial information.`,
    },
    PROPERTY_DISCLOSURES: {
        id: "PROPERTY_DISCLOSURES",
        name: "Property Disclosures",
        description: "Seller-provided statements outlining known defects, past repairs, or upcoming maintenance work that could impact the value or livability of a unit.",
    },
    INSURANCE_POLICIES: {
        id: "INSURANCE_POLICIES",
        name: "Insurance Policies",
        description: "Full policy documents outlining strata insurance coverage, including protection against property damage, liability claims, and loss of rental income. Essential for understanding what is covered and what is not.",
    },
    INSURANCE_SUMMARIES: {
        id: "INSURANCE_SUMMARIES",
        name: "Insurance Summaries",
        description: "Condensed overviews of insurance policies, including coverage limits, deductibles, renewal dates, and key terms. Useful for quick reference by owners and buyers.",
    },
    BUILDING_REPORTS: {
        id: "BUILDING_REPORTS",
        name: "Building Reports",
        description: "Engineering assessments, safety inspections, and regulatory compliance reports evaluating the condition of the strata building. These reports highlight any structural, plumbing, or electrical concerns.",
    },
    DEPRECIATION_REPORTS: {
        id: "DEPRECIATION_REPORTS",
        name: "Depreciation Reports",
        description: "Long-term planning documents that estimate future repair and replacement costs for major building components, such as roofing, elevators, and plumbing systems. Helps determine if the reserve fund is sufficient.",
    },
    REPAIR_PROJECTS: {
        id: "REPAIR_PROJECTS",
        name: "Repair Projects",
        description: "Records of past, current, and upcoming renovations or remediation projects, such as exterior waterproofing, balcony repairs, or foundation reinforcements. Includes project timelines and cost breakdowns.",
    },
    LANDSCAPING: {
        id: "LANDSCAPING",
        name: "Landscaping",
        description: "Documentation on groundskeeping, tree removal, garden maintenance, and outdoor improvement projects. Includes service contracts, site plans, and cost estimates.",
    },
    MEETING_MINUTES: {
        id: "MEETING_MINUTES",
        name: "Meeting Minutes",
        description: "Official records of discussions and decisions made at strata council meetings, AGMs (Annual General Meetings), and SGMs (Special General Meetings). These minutes document votes, financial updates, and policy changes. These minutes may also include appendices, such as financial statements or reports, which are not part of the minutes themselves but are referenced within them.",
    },
    COUNCIL_RESOLUTIONS: {
        id: "COUNCIL_RESOLUTIONS",
        name: "Council Resolutions",
        description: "Formal decisions passed by the strata council regarding property management, bylaw enforcement, major expenditures, and operational changes. These resolutions have a legal impact on strata governance.",
    },
    STRATA_NOTICES: {
        id: "STRATA_NOTICES",
        name: "Strata Notices",
        description: "Official communications from the strata council, including meeting notices, rule enforcement letters, maintenance updates, and emergency alerts.",
    },
    PARKING_ASSIGNMENTS: {
        id: "PARKING_ASSIGNMENTS",
        name: "Parking Assignments",
        description: "Documents outlining allocated parking spots for residents, visitor parking policies, and records of ownership or rental of parking spaces.",
    },
    STORAGE_LOCKERS_ASSIGNMENTS: {
        id: "STORAGE_LOCKERS_ASSIGNMENTS",
        name: "Storage Lockers Assignments",
        description: "Records of assigned storage lockers, including agreements, size details, and rules for use. Some documents may outline rental arrangements or disputes over locker assignments.",
    },
    STRATA_DOCUMENT_PACKAGE: {
        id: "STRATA_DOCUMENT_PACKAGE",
        name: "Strata Document Package",
        description: "A strata document package is a collection of documents related to a specific strata property, typically used during real estate transactions, property management, or due diligence. These packages often include key records such as the strata plan, bylaws, meeting minutes, financial statements, depreciation reports, insurance certificates, engineering reports, and other relevant documents that provide insight into the condition, rules, and financial health of the strata corporation. The documents can span many pages and may contain multiple sub-documents merged into a single PDF file.",
    },
    OTHER: {
        id: "OTHER",
        name: "Other",
        description: "A document that does not fit into any other category or does not appear to be related to a specific property.",
    },
};
//# sourceMappingURL=documentCategories.js.map